<template>
  <b-popover
    :delay="delay"
    :placement="placement"
    :target="target"
    :triggers="triggers"
    :custom-class="compactPopover ? 'hazard-popover-compact hazard-popover' : 'hazard-popover'"
  >
    <template #title>
      {{ (chemical?.name || chemical?.casrn || '') + (score && score?.hazardName ? ' | ' + score.hazardName : '') }}
    </template>

    <b-table-lite
      v-if="items?.length"
      :fields="scoreRecordFields"
      :items="items"
      :stacked="hazardCategory && firstOnly"
      borderless
      small
    >
      <template #[`cell(${scoreField})`]="data">
        <span
          :class="{
            'table-secondary': data.value === 'I',
            'table-warning-light': data.value === 'M',
            'table-success': data.value === 'L',
            'table-danger': data.value === 'VH',
            'table-orange': data.value === 'H',
            'authoritative':
              (data.item.authority || data.item.finalAuthority || data.item.listType) === 'Authoritative',
            'screening': (data.item.authority || data.item.finalAuthority) === 'Screening',
            'predicted': (data.item.authority || data.item.finalAuthority) === 'QSAR Model',
          }"
          class="px-2"
        >
          {{ data.value }}
        </span>
      </template>

      <template #cell()="data">
        <span>
          {{ data.value }}
        </span>
      </template>

      <template #cell(note)="data">
        <span v-html="data.value" />
      </template>

      <template #cell(rationale)="data">
        <span v-html="data.value" />
      </template>
    </b-table-lite>

    <div v-else>No records available</div>
  </b-popover>
</template>
<script>
import { HAZARD_URL } from '@/main'

export default {
  props: {
    target: {
      type: String,
      required: true,
    },
    triggers: {
      type: String,
      default: 'hover',
    },
    delay: {
      type: Number,
      default: 500,
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    firstOnly: {
      type: Boolean,
      default: false,
    },
    compactPopover: {
      type: Boolean,
      default: false,
    },
    hazardChemical: {
      default: null,
      validator(value) {
        return (
          value === null ||
          typeof value === 'string' ||
          value instanceof Promise ||
          (typeof value === 'object' && value !== null)
        )
      },
    },
    hazardCategory: {
      type: String,
      default: null,
    },
    scoreRecordFields: {
      type: Array,
      default: function () {
        if (!this.hazardCategory) return ['hazardName', 'finalScore', 'finalScoreSource']
        else if (this.firstOnly)
          return [
            { key: 'source', sortable: true },
            { key: 'listType', label: 'Authority', sortable: true },
            { key: 'score', sortable: true },
            { key: 'route', sortable: true },
            { key: 'category', sortable: true },
            { key: 'hazardCode', sortable: true },
            { key: 'hazardStatement' },
            { key: 'rationale' },
            { key: 'note' },
          ]
        else
          return [
            { key: 'source', sortable: true },
            { key: 'authority', sortable: true },
            { key: 'score', sortable: true },
          ]
      },
    },
  },

  data() {
    return {
      chemical: {
        name: null,
        casrn: null,
      },
      score: {
        hazardName: null,
        records: [],
      },
      scores: [],
      resolvedHazardChemical: null,
    }
  },

  watch: {
    hazardChemical: {
      immediate: true,
      handler() {
        this.resolveHazardChemical()
      },
    },
    hazardCategory() {
      this.applyHazardChemical(this.resolvedHazardChemical)
    },
  },

  computed: {
    items: function () {
      if (this.hazardCategory) return this.firstOnly ? this.score.records.slice(0, 1) : this.score.records
      else return this.scores
    },
    scoreField: function () {
      return this.hazardCategory ? 'score' : 'finalScore'
    },
  },

  methods: {
    getHazardChemical(id) {
      return this.$axios.get(`${HAZARD_URL}?query=${encodeURIComponent(id)}`).then((response) => {
        return response.data.hazardChemicals[0]
      })
    },
    resolveHazardChemical() {
      let hazardChemical =
        typeof this.hazardChemical === 'string' ? this.getHazardChemical(this.hazardChemical) : this.hazardChemical

      Promise.resolve(hazardChemical)
        .then((hc) => {
          this.resolvedHazardChemical = hc || null
          this.applyHazardChemical(this.resolvedHazardChemical)
        })
        .catch(() => {
          this.resolvedHazardChemical = null
          this.applyHazardChemical(null)
        })
    },
    applyHazardChemical(hazardChemical) {
      if (!hazardChemical) {
        this.chemical = {
          name: null,
          casrn: null,
        }
        this.score = {
          hazardName: null,
          records: [],
        }
        this.scores = []
        return
      }

      this.chemical = hazardChemical.chemical || {
        name: null,
        casrn: null,
      }
      this.scores = hazardChemical.scores || []

      if (this.hazardCategory) {
        this.score = this.scores.find((score) => score.hazardId === this.hazardCategory) || {
          hazardName: null,
          records: [],
        }
      } else {
        this.score = {
          hazardName: null,
          records: [],
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.hazard-popover {
  max-width: 800px !important;
  min-width: 600px;
  width: max-content !important;
}
::v-deep .table.b-table.b-table-stacked > tbody > tr > [data-label]::before {
  width: 25% !important;
}
::v-deep .table.b-table.b-table-stacked > tbody > tr > [data-label] > div {
  width: calc(100% - 25%) !important;
}
</style>
